import { Button, styled } from '@mui/material';
import { red } from '@mui/material/colors';
import { usePageMessages } from 'components/new/Page/usePageMessages';
import DataRoomGeneral from './General';
import { DataRoomStatus } from 'further-types/data-room';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DataRoomFormData, defaultData, schema } from './formData';
import { useDataRoomRequests } from './useDataRoomRequests';
import {
  mapDataRoomFormDataToUpdateRequest,
  mapDataRoomResponseToFormData,
} from './mapping';
import { DataRoomTab, dataRoomTabFields, dataRoomTabs } from './tabs';
import { match } from 'ts-pattern';
import VerticalTabs from 'components/new/VerticalTabs';
import { useVerticalTabs } from 'components/new/VerticalTabs/useVerticalTabs';
import FullHeightView from 'components/new/FullHeightView';
import { Column, Row } from 'further-ui/components/Layout';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import SiteTerms from './SiteTerms';
import Content from './Content';

type Props = {
  dataRoomId: string;
  setLiveDisabled?: boolean;
};

const TabContent = styled(Column)(({ theme }) => ({
  flex: 1,
  paddingTop: theme.spacing(theme.spacingSizes.lg),
  paddingBottom: theme.spacing(theme.spacingSizes.lg),
}));

const DataRoomEdit: React.FC<Props> = ({ dataRoomId, setLiveDisabled }) => {
  const { data, update, updating } = useDataRoomRequests(dataRoomId);
  const [canUpdate] = useHasPermissions([RolePermission.EditDataRoom]);

  const { control, formState, handleSubmit } = useForm<DataRoomFormData>({
    shouldFocusError: true,
    resolver: zodResolver(schema),
    values: data ? mapDataRoomResponseToFormData(data) : defaultData,
    criteriaMode: 'all',
  });

  const { addMessage, removeMessage } = usePageMessages();

  const showFormErrors = () =>
    addMessage({
      key: 'form-errors',
      variant: 'error',
      centered: true,
      content:
        'One or more fields have errors, please correct them and try again.',
    });

  const handleSave = (data: DataRoomFormData) => {
    removeMessage('form-errors');
    update(mapDataRoomFormDataToUpdateRequest(dataRoomId, data));
  };

  const formValues = useWatch({ control });
  const isFormValidForLive = schema.safeParse({
    ...formValues,
    status: DataRoomStatus.Live,
  }).success;

  const { errors } = formState;
  const { selectedTab, setSelectedTab, tabs } = useVerticalTabs(
    DataRoomTab.General,
    {
      tabs: dataRoomTabs,
      getDotColor: (tab) =>
        dataRoomTabFields[tab].some((field) => field in errors)
          ? red[500]
          : undefined,
    },
  );

  return (
    <FullHeightView
      innerPaddingBottom="xl"
      actionBar={
        formState.isDirty && canUpdate ? (
          <Row justify="flex-end">
            <Button
              onClick={handleSubmit(handleSave, showFormErrors)}
              variant="contained"
              size="large"
              loading={updating}
            >
              Save
            </Button>
          </Row>
        ) : null
      }
    >
      <VerticalTabs
        responsive
        fullHeight
        tabs={tabs}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        renderContent={(tab) => (
          <TabContent>
            {match(tab)
              .with(DataRoomTab.General, () => (
                <DataRoomGeneral
                  setLiveDisabled={!isFormValidForLive || setLiveDisabled}
                  control={control}
                />
              ))
              .with(DataRoomTab.SiteTerms, () => (
                <SiteTerms control={control} />
              ))
              .with(DataRoomTab.Content, () => (
                <Content
                  control={control}
                  defaultLogoUri={data?.logoSettings?.defaultLogoUri ?? ''}
                  panelColor={data?.palette?.panelColor}
                />
              ))
              .with(DataRoomTab.Documents, () => null)
              .with(DataRoomTab.Access, () => null)
              .exhaustive()}
          </TabContent>
        )}
      />
    </FullHeightView>
  );
};

export default DataRoomEdit;
